/**
 * The per-jurisdiction tax rows, as the settings screen reads and writes them.
 *
 * The API half is `fieldiq-api/src/lib/company-tax-rates.ts` (the queries) and
 * `company-tax.ts` (the resolver and the write validator), behind
 * `GET/PUT /settings/tax-rates` and `DELETE /settings/tax-rates/:id`. Nothing
 * here computes tax; the API resolves it and the documents carry snapshots.
 * This module is about which row a human is looking at and whether the form
 * they are filling in can be saved.
 *
 * ── THE THING THE SCREEN HAS TO MAKE VISIBLE ────────────────────────────────
 *
 * `effective_from` is part of the unique key `(company, jurisdiction,
 * effective_from)`, and the resolver picks the newest row dated on or before
 * the document. So there are two completely different operations wearing the
 * same shape:
 *
 *   a rate CHANGE      a NEW row with a later start date. The old row keeps
 *                      answering for work dated before the change, which is
 *                      what makes reprinting an old invoice reproduce the old
 *                      rate.
 *   a CORRECTION       an edit of an existing row. It rewrites what every
 *                      document dated on or after that row's start date will
 *                      resolve to — history included.
 *
 * PUT upserts on the key, so an "edit" that also moves the date silently
 * becomes a change and leaves the original row behind. `rowEffect()` below and
 * the two separate buttons on the page are how that distinction is put on
 * screen rather than left in a docblock.
 */

import { toRegionCode, regionName, isRegionCode } from '../components/ProvinceSelect';
import { isTaxSystem, requiredRates, type TaxSystem } from '../constants/tax-systems';

/**
 * One `company_tax_rates` row as `formatTaxRateRow` serialises it.
 *
 * SNAKE_CASE ON A READ, which is the exception to the repo's usual asymmetry —
 * this serialiser passes the column names through rather than camelising them.
 * Typed to the actual payload, not to the convention (CLAUDE.md, "type the
 * interface to the actual payload").
 *
 * The two rates are NUMERIC(6,3) since API 0483 (QST is 9.975%) and arrive as
 * STRINGS ('5.000', '0.000') from
 * pg, not numbers. `null` is a real and different state from '0.00': measured
 * on production 2026-09-03, six of the twelve seeded rows carry a NULL rate 2
 * and five carry '0.00'. The first means nobody stated a second rate, the
 * second means the tenant stated that it is zero.
 */
export interface TaxRateRow {
  id: string;
  jurisdiction: string;
  /** 'YYYY-MM-DD'. Selected by `effective_from::text` on the API, so it is a
   *  calendar day string and never an instant. Do not put it through `new
   *  Date()` — see `calendar-date.ts`. */
  effective_from: string;
  tax_system: string | null;
  tax_rate_1: string | number | null;
  tax_rate_2: string | number | null;
  tax_rate_1_label: string | null;
  tax_rate_2_label: string | null;
  created_at?: string;
  updated_at?: string;
}

/** `GET /settings/tax-rates` → `{ success, data: { home_jurisdiction, rows } }`. */
export interface TaxRatesPayload {
  /** `companies.province_state`, uppercased. The jurisdiction a document with
   *  no province of its own resolves as. null when the tenant has no home. */
  home_jurisdiction: string | null;
  rows: TaxRateRow[];
}

/** The body `PUT /settings/tax-rates` accepts. Writes are snake_case. */
export interface TaxRateWrite {
  jurisdiction: string;
  effective_from: string;
  tax_system: TaxSystem;
  tax_rate_1: number | null;
  tax_rate_2: number | null;
  tax_rate_1_label: string | null;
  tax_rate_2_label: string | null;
}

/**
 * Which of a jurisdiction's rows is answering today.
 *
 *   in_effect    the newest row dated on or before today — the one a document
 *                dated today resolves to
 *   scheduled    dated in the future; it takes over on its own start date
 *   superseded   a later row dated on or before today has taken over. It still
 *                answers for work dated in ITS window, so it is history, not
 *                dead weight — which is why the page never offers to tidy it
 *                away.
 *
 * Exactly one row per jurisdiction can be `in_effect`, and a jurisdiction whose
 * rows all start in the future has none.
 */
export type RowEffect = 'in_effect' | 'scheduled' | 'superseded';

export const ROW_EFFECT_LABELS: Record<RowEffect, string> = {
  in_effect: 'In effect',
  scheduled: 'Scheduled',
  superseded: 'Superseded',
};

/**
 * Mirrors `selectTaxRateRow` in the API: the greatest `effective_from` that is
 * on or before the date. Compared as STRINGS — 'YYYY-MM-DD' sorts correctly
 * lexicographically, and building a Date to compare two calendar days is the
 * bug `calendar-date.ts` exists to stop.
 */
export function rowEffect(
  row: TaxRateRow,
  rowsForJurisdiction: readonly TaxRateRow[],
  // The TENANT's today ('YYYY-MM-DD', todayCalendarDate(useTenantTimeZone())).
  // Required, with no default: a default would have to pick a zone, and the
  // only one available here is the device's.
  today: string,
): RowEffect {
  if (row.effective_from > today) return 'scheduled';
  let newestApplied = '';
  for (const r of rowsForJurisdiction) {
    if (r.jurisdiction !== row.jurisdiction) continue;
    if (r.effective_from > today) continue;
    if (r.effective_from > newestApplied) newestApplied = r.effective_from;
  }
  return row.effective_from === newestApplied ? 'in_effect' : 'superseded';
}

/**
 * The row that would price a document dated today if `removed` were deleted —
 * null when nothing else in that jurisdiction applies.
 *
 * WHY THIS IS NOT "is it the in-effect row". Deleting the in-effect row is
 * usually harmless: the row underneath it takes over, and the tenant is still
 * priced by a stated rate. Only when there is nothing underneath does the
 * resolver fall through to the legacy company columns, and that is the case
 * worth interrupting somebody over.
 *
 * Found by deleting a row on a real screen and watching the row below it go
 * straight back to "In effect" under a warning that had just said pricing
 * would fall back — a true sentence about the wrong row.
 */
export function rowInEffectAfterRemoving(
  removed: TaxRateRow,
  rows: readonly TaxRateRow[],
  // The TENANT's today ('YYYY-MM-DD', todayCalendarDate(useTenantTimeZone())).
  // Required, with no default: a default would have to pick a zone, and the
  // only one available here is the device's.
  today: string,
): TaxRateRow | null {
  let best: TaxRateRow | null = null;
  for (const r of rows) {
    if (r.id === removed.id) continue;
    if (r.jurisdiction !== removed.jurisdiction) continue;
    if (r.effective_from > today) continue;
    if (best === null || r.effective_from > best.effective_from) best = r;
  }
  return best;
}

export interface JurisdictionGroup {
  jurisdiction: string;
  /** Display name for the code, or the code itself when it is not one we know. */
  name: string;
  /** Newest start date first, so a rate change lands at the top of its group. */
  rows: TaxRateRow[];
}

/**
 * Group the flat list by jurisdiction. The home jurisdiction sorts first when
 * given, then the rest alphabetically — the tenant's own province is the row
 * they came here to read, and every tenant in the estate has exactly one row
 * today, so it would otherwise sit wherever the alphabet put it.
 */
export function groupByJurisdiction(
  rows: readonly TaxRateRow[],
  homeJurisdiction?: string | null,
): JurisdictionGroup[] {
  const byCode = new Map<string, TaxRateRow[]>();
  for (const r of rows) {
    const list = byCode.get(r.jurisdiction);
    if (list) list.push(r);
    else byCode.set(r.jurisdiction, [r]);
  }
  const home = homeJurisdiction ? homeJurisdiction.toUpperCase() : null;
  return Array.from(byCode.entries())
    .map(([jurisdiction, list]) => ({
      jurisdiction,
      name: regionName(jurisdiction),
      rows: [...list].sort((a, b) => b.effective_from.localeCompare(a.effective_from)),
    }))
    .sort((a, b) => {
      if (a.jurisdiction === home) return -1;
      if (b.jurisdiction === home) return 1;
      return a.jurisdiction.localeCompare(b.jurisdiction);
    });
}

/** A rate for the table. Lives with the other display formatters. */
export { fmtTaxRate } from './display-labels';

/** The rate's label, or the em dash. Never the raw column when it is blank. */
export function fmtRateLabel(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  return t === '' ? '—' : t;
}

/** The form, as the inputs hold it — every field a string, blank meaning blank. */
export interface TaxRateFormValues {
  jurisdiction: string;
  effective_from: string;
  tax_system: string;
  tax_rate_1: string;
  tax_rate_2: string;
  tax_rate_1_label: string;
  tax_rate_2_label: string;
}

export const EMPTY_TAX_RATE_FORM: TaxRateFormValues = {
  jurisdiction: '',
  effective_from: '',
  tax_system: '',
  tax_rate_1: '',
  tax_rate_2: '',
  tax_rate_1_label: '',
  tax_rate_2_label: '',
};

export type TaxRateFormField = keyof TaxRateFormValues;

export type TaxRateFormResult =
  | { ok: true; payload: TaxRateWrite }
  | { ok: false; field: TaxRateFormField; error: string };

/**
 * Validate the form and produce the PUT body.
 *
 * ── THE JURISDICTION IS FOLDED HERE, AT THE CLIENT DOOR ─────────────────────
 *
 * `company_tax_rates.jurisdiction` is `VARCHAR(2)` with a
 * `CHECK (jurisdiction ~ '^[A-Z]{2}$')`, and migration 0369 installs the same
 * region-normalising BEFORE trigger every other region column carries. On a
 * two-character column that trigger can only fold CASE — Postgres applies the
 * column length at input conversion, BEFORE any BEFORE trigger runs, so
 * 'Saskatchewan' is a `22001 value too long` and never reaches the fold. The
 * trigger is belt; folding at the door is the braces, and it is the half that
 * works for a full name.
 *
 * `toRegionCode` is the same fold `ProvinceSelect` uses, so a stored value in
 * the pre-0365 spelling and a freshly picked code both end up as the code. An
 * input it does not recognise is REFUSED rather than sent — it never stores a
 * guess.
 *
 * The rate rules mirror `checkTaxCoherence` in the API, so the form refuses
 * what the API would refuse instead of round-tripping to find out. The API
 * still validates independently; this is not a substitute for it.
 */
export function validateTaxRateForm(values: TaxRateFormValues): TaxRateFormResult {
  const rawJurisdiction = values.jurisdiction.trim();
  if (!rawJurisdiction) {
    return { ok: false, field: 'jurisdiction', error: 'Choose a province or state.' };
  }
  const code = toRegionCode(rawJurisdiction);
  if (!isRegionCode(code)) {
    return {
      ok: false,
      field: 'jurisdiction',
      error: `"${rawJurisdiction}" is not a province or state. Use the two-letter code (AB, BC, SK, TX …) or the full name.`,
    };
  }

  const from = values.effective_from.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
    return { ok: false, field: 'effective_from', error: 'Enter a start date.' };
  }
  // Rejects '2026-02-31', which a Date would silently roll forward to March 3.
  const [y, m, d] = from.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    return { ok: false, field: 'effective_from', error: `${from} is not a real date.` };
  }

  if (!isTaxSystem(values.tax_system)) {
    return { ok: false, field: 'tax_system', error: 'Choose a tax system.' };
  }
  const system: TaxSystem = values.tax_system;
  const need = requiredRates(system);

  const rate = (
    raw: string,
    field: 'tax_rate_1' | 'tax_rate_2',
    required: boolean,
    what: string,
  ): { ok: true; value: number | null } | { ok: false; field: TaxRateFormField; error: string } => {
    const t = raw.trim();
    if (t === '') {
      // Blank is ABSENT, not zero. A required rate left blank is refused here
      // for the same reason the API refuses it: the row would save and every
      // document under it would then read "Tax rate not configured".
      if (required) {
        return {
          ok: false,
          field,
          error: `${what} needs a rate under ${values.tax_system.replace(/_/g, ' ')}. Enter 0 if none applies.`,
        };
      }
      return { ok: true, value: null };
    }
    const n = Number(t);
    if (!Number.isFinite(n)) return { ok: false, field, error: `${what} must be a number.` };
    if (n < 0 || n > 100) return { ok: false, field, error: `${what} must be between 0 and 100.` };
    return { ok: true, value: n };
  };

  const r1 = rate(values.tax_rate_1, 'tax_rate_1', need.rate1, 'Rate 1');
  if (!r1.ok) return r1;
  const r2 = rate(values.tax_rate_2, 'tax_rate_2', need.rate2, 'Rate 2');
  if (!r2.ok) return r2;

  const label = (
    raw: string,
    field: 'tax_rate_1_label' | 'tax_rate_2_label',
    what: string,
  ): { ok: true; value: string | null } | { ok: false; field: TaxRateFormField; error: string } => {
    const t = raw.trim();
    // 50 matches the column and the six varchar(50) snapshot columns this
    // value is copied onto. It was 20 once, which turned
    // 'GST/HST Combined Rate' into a quiet 400 with no 22001 in the logs.
    if (t.length > 50) return { ok: false, field, error: `${what} must be 50 characters or fewer.` };
    return { ok: true, value: t || null };
  };

  const l1 = label(values.tax_rate_1_label, 'tax_rate_1_label', 'Rate 1 label');
  if (!l1.ok) return l1;
  const l2 = label(values.tax_rate_2_label, 'tax_rate_2_label', 'Rate 2 label');
  if (!l2.ok) return l2;

  return {
    ok: true,
    payload: {
      jurisdiction: code,
      effective_from: from,
      tax_system: system,
      tax_rate_1: r1.value,
      tax_rate_2: r2.value,
      tax_rate_1_label: l1.value,
      tax_rate_2_label: l2.value,
    },
  };
}

/** An existing row, opened for correction. Every field as the row holds it. */
export function formValuesFromRow(row: TaxRateRow): TaxRateFormValues {
  return {
    jurisdiction: row.jurisdiction,
    effective_from: row.effective_from,
    tax_system: row.tax_system ?? '',
    // An absent rate opens BLANK, not as '0'. Typing a 0 into a blank field is
    // the tenant stating zero; showing them a 0 they never typed is the app
    // stating it for them.
    tax_rate_1: row.tax_rate_1 === null || row.tax_rate_1 === undefined ? '' : String(row.tax_rate_1),
    tax_rate_2: row.tax_rate_2 === null || row.tax_rate_2 === undefined ? '' : String(row.tax_rate_2),
    tax_rate_1_label: row.tax_rate_1_label ?? '',
    tax_rate_2_label: row.tax_rate_2_label ?? '',
  };
}

/**
 * A rate CHANGE for a jurisdiction that already has rows: the jurisdiction
 * carries over because it is what is being changed, and NOTHING ELSE DOES.
 *
 * Copying the current row's rates forward would be the app suggesting an
 * answer to the only question the office came here to answer, and a
 * pre-filled figure that is silently wrong reads exactly like one somebody
 * checked. The date is blank for the same reason — the start date IS the
 * change.
 */
export function changeFormForJurisdiction(jurisdiction: string): TaxRateFormValues {
  return { ...EMPTY_TAX_RATE_FORM, jurisdiction };
}

/**
 * One row of `GET /settings/standard-tax-rates` — the statutory general rate
 * for a Canadian province or territory, from the API's
 * `src/lib/standard-tax-rates.ts`. The SAME table tenant creation uses, so an
 * office auto-filling Alberta gets exactly what a new Alberta tenant starts on.
 * Rates arrive as NUMBERS here (a constant, not a column). US states are never
 * in it: a US rate is state + county + city.
 */
export interface StandardTaxRate {
  jurisdiction: string;
  tax_system: TaxSystem;
  tax_rate_1: number;
  tax_rate_2: number | null;
  tax_rate_1_label: string;
  tax_rate_2_label: string | null;
}

/** 1 January of the year `today` falls in, or '' when today is unknown. */
export function startOfYear(today: string | null | undefined): string {
  return today && /^\d{4}-/.test(today) ? `${today.slice(0, 4)}-01-01` : '';
}

/**
 * The "Add jurisdiction" form for a picked province.
 *
 * A Canadian province or territory fills its statutory system, rates and
 * labels from the API's table; every field stays editable. Anything with no
 * standard row (every US state) fills nothing — the rate there is local.
 * The start date is 1 January of the tenant's current year, not today: a
 * tenant stating its rate is stating the one it has been charging, and a row
 * dated today leaves this year's earlier work with nothing to resolve to.
 */
export function addFormForJurisdiction(
  jurisdiction: string,
  standard: readonly StandardTaxRate[] | undefined,
  effectiveFrom: string,
): TaxRateFormValues {
  const s = standard?.find(r => r.jurisdiction === jurisdiction);
  if (!s) return { ...EMPTY_TAX_RATE_FORM, jurisdiction, effective_from: effectiveFrom };
  return {
    jurisdiction,
    effective_from: effectiveFrom,
    tax_system: s.tax_system,
    tax_rate_1: String(s.tax_rate_1),
    // null is "no second rate", which the form spells as blank — never '0'.
    tax_rate_2: s.tax_rate_2 === null ? '' : String(s.tax_rate_2),
    tax_rate_1_label: s.tax_rate_1_label,
    tax_rate_2_label: s.tax_rate_2_label ?? '',
  };
}

/**
 * The page's ONE status line, or null when nothing is wrong.
 *
 * Replaces two stacked banners (an amber "No jurisdiction row applies today"
 * and a red "Tax is not configured") that often showed together and said the
 * same thing twice. A line is shown only when the home jurisdiction's tax is
 * actually missing something, and it always carries the one action that fixes
 * it:
 *
 *   no home row today, tax unknown   "Add your tax rates to bill in AB — …"  [Add AB]
 *   no home row today, still priced  "Add your tax rates to bill in AB."    [Add AB]
 *   a home row missing a rate        "PST missing for BC — …"               [Fix]
 *   no home province at all          a pointer to the field above, no button
 */
export type TaxStatusAction =
  | { kind: 'add'; jurisdiction: string; label: string }
  | { kind: 'correct'; row: TaxRateRow; label: string };

export interface TaxStatusLine {
  message: string;
  action: TaxStatusAction | null;
}

const UNKNOWN_TAIL = ' — invoices will show tax as unknown.';

export function taxStatusLine(args: {
  taxSource: string | null | undefined;
  unconfiguredReason: string | null | undefined;
  homeJurisdiction: string | null | undefined;
  rows: readonly TaxRateRow[];
  /** The tenant's today; null when its zone is unknown. */
  today: string | null;
}): TaxStatusLine | null {
  const { taxSource, unconfiguredReason, homeJurisdiction, rows, today } = args;
  const unconfigured = !!unconfiguredReason;
  if (!unconfigured && taxSource !== 'company_columns') return null;

  const home = homeJurisdiction ? homeJurisdiction.toUpperCase() : null;
  if (!home) {
    return unconfigured
      ? { message: `Choose your province above, then add its tax rates${UNKNOWN_TAIL}`, action: null }
      : null;
  }

  const homeRows = rows.filter(r => r.jurisdiction === home);
  const inEffect = today ? homeRows.find(r => rowEffect(r, homeRows, today) === 'in_effect') ?? null : null;

  if (unconfigured && inEffect) {
    return {
      message: `${missingRateName(inEffect)} missing for ${home}${UNKNOWN_TAIL}`,
      action: { kind: 'correct', row: inEffect, label: 'Fix' },
    };
  }
  return {
    message: `Add your tax rates to bill in ${home}${unconfigured ? UNKNOWN_TAIL : '.'}`,
    action: { kind: 'add', jurisdiction: home, label: `Add ${home}` },
  };
}

/** What an incomplete row lacks, named the way the row names it. */
function missingRateName(row: TaxRateRow): string {
  const blank = (v: unknown) => v === null || v === undefined || v === '';
  if (!isTaxSystem(row.tax_system)) return 'Tax system';
  const need = requiredRates(row.tax_system);
  if (need.rate1 && blank(row.tax_rate_1)) return row.tax_rate_1_label?.trim() || (row.tax_system === 'HST' ? 'HST' : row.tax_system === 'US_state' ? 'State tax' : 'GST');
  if (need.rate2 && blank(row.tax_rate_2)) return row.tax_rate_2_label?.trim() || 'PST';
  return 'A tax rate';
}
